import { useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, AlertCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const EditRSVPContributionModal = ({ event, rsvp, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [step, setStep] = useState('edit'); // 'edit' or 'payment'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    rsvp_status: rsvp.rsvp_status,
    message: rsvp.message || '',
    adults_male: rsvp.adults_male || 0,
    adults_female: rsvp.adults_female || 0,
    kids_male: rsvp.kids_male || 0,
    kids_female: rsvp.kids_female || 0,
    additional_contribution: '',
    contribution_goal_id: '',
    payment_method: 'razorpay'
  });

  const totalGuests = formData.adults_male + formData.adults_female + formData.kids_male + formData.kids_female;
  const maxEdits = 3;
  const editsRemaining = maxEdits - rsvp.edit_count;

  const handleUpdate = async () => {
    // Validate guest count for "Yes" and "Maybe" RSVP status
    if (formData.rsvp_status === 'yes' || formData.rsvp_status === 'maybe') {
      const totalGuestCount = formData.adults_male + formData.adults_female + formData.kids_male + formData.kids_female;
      if (totalGuestCount === 0) {
        toast.error('Please enter the number of guests coming. At least one guest must be specified.');
        return;
      }
    }

    setLoading(true);
    try {
      // Update RSVP status and guest count
      await axios.put(
        `${API}/rsvps/${rsvp.id}`,
        {
          rsvp_status: formData.rsvp_status,
          message: formData.message || undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // If additional contribution is added, proceed to payment
      if (formData.additional_contribution && parseFloat(formData.additional_contribution) > 0) {
        if (!formData.contribution_goal_id) {
          toast.error('Please select a contribution goal');
          setLoading(false);
          return;
        }
        toast.success('RSVP updated! Proceeding to payment...');
        setStep('payment');
      } else {
        toast.success('RSVP updated successfully!');
        onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update RSVP');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Create new RSVP for additional contribution
      const response = await axios.post(
        `${API}/rsvps`,
        {
          event_id: event.id,
          rsvp_status: formData.rsvp_status,
          contribution_goal_id: formData.contribution_goal_id,
          contribution_amount: parseFloat(formData.additional_contribution),
          message: 'Additional contribution',
          adults_male: 0,
          adults_female: 0,
          kids_male: 0,
          kids_female: 0
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const newRsvpId = response.data.id;

      // Initiate payment
      const initResponse = await axios.post(
        `${API}/payments/initiate`,
        {
          rsvp_id: newRsvpId,
          payment_method: formData.payment_method
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (initResponse.data.success) {
        toast.success(`${formData.payment_method === 'razorpay' ? 'Razorpay' : 'PayPal'} payment initiated (Mock)`);

        // Verify payment
        const verifyResponse = await axios.post(
          `${API}/payments/verify`,
          {
            rsvp_id: newRsvpId,
            payment_id: initResponse.data.order.order_id,
            payment_method: formData.payment_method
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (verifyResponse.data.success) {
          toast.success('Additional contribution successful!');
          onSuccess();
        }
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already RSVP')) {
        toast.error('You can only make one contribution per event. Your RSVP has been updated.');
        onSuccess();
      } else {
        toast.error(error.response?.data?.detail || 'Payment failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-rsvp-contribution-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {step === 'edit' ? 'Edit RSVP & Contribution' : 'Add Contribution'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {step === 'edit' ? (
            <>
              {/* Edit Limit Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 font-semibold">
                    {editsRemaining} edit{editsRemaining !== 1 ? 's' : ''} remaining
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    You can edit your RSVP {maxEdits} times total after your initial response
                  </p>
                </div>
              </div>

              {/* Current Contribution Display */}
              {rsvp.contribution_amount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Your Current Contribution</p>
                  <p className="text-2xl font-bold text-green-600">
                    ₹{rsvp.contribution_amount.toLocaleString()}
                  </p>
                  {rsvp.contribution_goal_title && (
                    <p className="text-xs text-gray-500 mt-1">to {rsvp.contribution_goal_title}</p>
                  )}
                </div>
              )}

              {/* RSVP Status */}
              <div className="space-y-4">
                <Label>Update your attendance status *</Label>
                <RadioGroup
                  value={formData.rsvp_status}
                  onValueChange={(value) => setFormData({ ...formData, rsvp_status: value })}
                  data-testid="edit-rsvp-status-selector"
                >
                  <div className="flex items-center space-x-2 border-2 border-green-100 p-3 rounded-lg bg-green-50">
                    <RadioGroupItem value="yes" id="edit-yes" />
                    <Label htmlFor="edit-yes" className="flex-1 cursor-pointer">
                      <span className="font-medium text-green-700">✓ Yes, Coming</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border-2 border-red-100 p-3 rounded-lg bg-red-50">
                    <RadioGroupItem value="no" id="edit-no" />
                    <Label htmlFor="edit-no" className="flex-1 cursor-pointer">
                      <span className="font-medium text-red-700">✗ No, Not coming</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border-2 border-yellow-100 p-3 rounded-lg bg-yellow-50">
                    <RadioGroupItem value="maybe" id="edit-maybe" />
                    <Label htmlFor="edit-maybe" className="flex-1 cursor-pointer">
                      <span className="font-medium text-yellow-700">? Maybe</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Guest Count (Read-only display with note) */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900 mb-2">Current Guest Count</p>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div><span className="text-gray-600">Adults (M):</span> <span className="font-bold">{rsvp.adults_male}</span></div>
                  <div><span className="text-gray-600">Adults (F):</span> <span className="font-bold">{rsvp.adults_female}</span></div>
                  <div><span className="text-gray-600">Kids (M):</span> <span className="font-bold">{rsvp.kids_male}</span></div>
                  <div><span className="text-gray-600">Kids (F):</span> <span className="font-bold">{rsvp.kids_female}</span></div>
                </div>
                <p className="text-lg font-bold text-blue-700">Total: {rsvp.total_guests}</p>
                <p className="text-xs text-blue-600 mt-2">Note: Guest count cannot be edited after initial RSVP</p>
              </div>

              {/* Additional Contribution */}
              <div className="space-y-3 border-2 border-purple-100 p-4 rounded-lg">
                <Label className="text-base font-semibold">Add Additional Contribution (Optional)</Label>
                
                <div className="space-y-2">
                  <Label htmlFor="contribution_goal">Select Goal</Label>
                  <select
                    id="contribution_goal"
                    className="w-full p-2 border rounded-md"
                    value={formData.contribution_goal_id}
                    onChange={(e) => setFormData({ ...formData, contribution_goal_id: e.target.value })}
                    data-testid="contribution-goal-select"
                  >
                    <option value="">Choose a goal</option>
                    {event.contribution_goals.map((goal) => (
                      <option key={goal.id} value={goal.id}>
                        {goal.title} (₹{goal.current_amount.toLocaleString()} / ₹{goal.target_amount.toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional_amount">Additional Amount (₹)</Label>
                  <Input
                    id="additional_amount"
                    type="number"
                    placeholder="Enter amount"
                    value={formData.additional_contribution}
                    onChange={(e) => setFormData({ ...formData, additional_contribution: e.target.value })}
                    min="1"
                    data-testid="additional-contribution-input"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Update Message (Optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Update your message to the host..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  data-testid="edit-message-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onClose}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
                  onClick={handleUpdate}
                  disabled={loading}
                  data-testid="update-rsvp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update RSVP'
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Payment Step */}
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Additional Contribution Amount</p>
                <p className="text-3xl font-bold text-green-600">
                  ₹{parseFloat(formData.additional_contribution).toLocaleString()}
                </p>
              </div>

              <div className="space-y-4">
                <Label>Select Payment Method</Label>
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <div className="flex items-center space-x-2 border-2 border-purple-100 p-3 rounded-lg">
                    <RadioGroupItem value="razorpay" id="razorpay" />
                    <Label htmlFor="razorpay" className="flex-1 cursor-pointer">
                      <div className="flex items-center">
                        <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
                        <span className="font-medium">Razorpay (UPI, Cards, etc.)</span>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border-2 border-purple-100 p-3 rounded-lg">
                    <RadioGroupItem value="paypal" id="paypal" />
                    <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                      <div className="flex items-center">
                        <CreditCard className="h-5 w-5 mr-2 text-blue-800" />
                        <span className="font-medium">PayPal</span>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                className="w-full bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
                onClick={handlePayment}
                disabled={loading}
                data-testid="payment-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay ₹${parseFloat(formData.additional_contribution).toLocaleString()}`
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditRSVPContributionModal;
